import { IssuedItemFileDto } from '@treasury/api/channel';
import { Nullable } from '@treasury/utils';
import { DomainEntity } from '../../../shared/types';

export class IssuedItemsFile extends DomainEntity<Nullable<IssuedItemFileDto>> {
    constructor(dto: Nullable<IssuedItemFileDto> = {} as IssuedItemFileDto) {
        super(dto);
    }

    public get id() {
        return this.dto?.issuedItemFileUniqueId || '';
    }

    public get uniqueId() {
        return this.dto?.issuedItemFileUniqueId || '';
    }

    public get fileName() {
        return this.dto?.fileName || '';
    }

    public get fileSize() {
        return this.dto?.fileSize || 0;
    }

    public get displayFileSize() {
        const bytes = this.fileSize;
        if (bytes === 0) {
            if (!this.isTransmitted) {
                return '';
            }
            return '0 B';
        }

        if (bytes >= 1000000) {
            return `${(bytes / 1000000).toFixed(1)} MB`;
        } else if (bytes >= 1000) {
            return `${(bytes / 1000).toFixed(1)} KB`;
        } else {
            return `${bytes} B`;
        }
    }

    public get source() {
        return this.dto?.uploadedByUserName || '';
    }

    public get receivedDate() {
        return this.dto?.uploadedDate ? new Date(this.dto.uploadedDate) : undefined;
    }

    public get processedDate() {
        if (!this.isTransmitted) {
            return this.receivedDate;
        }
        return this.dto?.processedDate ? new Date(this.dto.processedDate) : undefined;
    }

    public get processedBy() {
        return this.dto?.processedByUserName || '';
    }

    public get totalIssues() {
        return this.dto?.totalIssueCount || 0;
    }

    public get totalVoids() {
        return this.dto?.totalVoidCount || 0;
    }

    public get totalItems() {
        return this.dto?.totalItemCount || 0;
    }

    public get isProcessed() {
        return !!this.dto?.processedDate;
    }

    public get isExpired() {
        return this.dto?.deleted || false;
    }

    public get isProcessable() {
        return this.dto?.canProcess || false;
    }

    public get hasValidationError() {
        return this.dto?.isFileContentInvalid || false;
    }

    public get canView() {
        return this.dto?.canView || false;
    }

    public get canProcess() {
        return this.dto?.canProcess || false;
    }

    public get canDelete() {
        return this.dto?.canDelete || false;
    }

    public get isFileContentInvalid() {
        return this.dto?.isFileContentInvalid || false;
    }

    public get createdBy() {
        return this.dto?.uploadedByUserName || '';
    }

    public get createdDate() {
        return this.dto?.uploadedDate ? new Date(this.dto.uploadedDate) : undefined;
    }

    public get updatedBy() {
        return this.dto?.processedByUserName || '';
    }

    public get updatedDate() {
        return this.dto?.processedDate ? new Date(this.dto.processedDate) : undefined;
    }

    public get isTransmitted() {
        return this.createdBy === 'System';
    }

    public static createFromDto(dto: Nullable<IssuedItemFileDto>): IssuedItemsFile {
        return new IssuedItemsFile(dto);
    }

    public createDefault(): this {
        return new IssuedItemsFile() as this;
    }
}

export function createIssuedItemsFile(dto?: Nullable<IssuedItemFileDto>): IssuedItemsFile {
    return new IssuedItemsFile(dto);
}
